/**
 * Utilitaires image pour le CreatorsPanel.
 *
 * `fileToResizedDataUrl` lit un File depuis un <input type="file">, le
 * decodes via Image + canvas, le redimensionne pour que la dimension max
 * soit `maxDim` (defaut 256), et retourne un data URL JPEG. Garde l'aspect
 * ratio du source.
 *
 * Pourquoi : les avatars vivent dans localStorage tant que le backend
 * n'est pas branche. Une photo brute 4MB d'un iPhone explose le quota
 * localStorage en quelques creators. 256x256 JPEG = ~10-30KB → safe.
 */
export async function fileToResizedDataUrl(
  file: File,
  maxDim = 256,
  quality = 0.85,
): Promise<string> {
  const objectUrl = URL.createObjectURL(file);
  try {
    const img = await loadImage(objectUrl);
    const { width, height } = scaleToFit(img.naturalWidth, img.naturalHeight, maxDim);

    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("canvas 2d context unavailable");
    ctx.drawImage(img, 0, 0, width, height);
    return canvas.toDataURL("image/jpeg", quality);
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("image load failed"));
    img.src = src;
  });
}

function scaleToFit(w: number, h: number, max: number): { width: number; height: number } {
  if (w <= max && h <= max) return { width: w, height: h };
  const ratio = w / h;
  if (w >= h) {
    return { width: max, height: Math.round(max / ratio) };
  }
  return { width: Math.round(max * ratio), height: max };
}

/**
 * Meme redimensionnement, mais rend un Blob televersable.
 *
 * `fileToResizedDataUrl` sert aux avatars stockes en base64 ; ici on POSTe un
 * vrai fichier, et la cible est une photo d'accueil Telegram : 1280 px suffit
 * largement, et une photo brute d'iPhone (4 Mo) ferait ramer chaque /start.
 */
export async function fileToResizedBlob(
  file: File,
  maxDim = 1280,
  quality = 0.85,
): Promise<Blob> {
  const objectUrl = URL.createObjectURL(file);
  try {
    const img = await loadImage(objectUrl);
    const { width, height } = scaleToFit(img.naturalWidth, img.naturalHeight, maxDim);

    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("canvas 2d context unavailable");
    ctx.drawImage(img, 0, 0, width, height);

    return await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(
        (blob) => (blob ? resolve(blob) : reject(new Error("canvas toBlob failed"))),
        "image/jpeg",
        quality,
      );
    });
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}
